import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTableModule } from 'ng-zorro-antd/table';
import { debounceTime, forkJoin, mergeMap, Subject, take } from 'rxjs';
import { initMethodologyData } from '../../../../../../backend/src/core/_init-data/data/init-methodology.data';
import { OnboardingSurveyData } from '../../../../../../backend/src/core/_init-data/init-data.gate';
import { CoreItemService } from '../../../core/core-item/core-item.service';
import { GoalService } from '../../../core/goal/goal.service';
import { MethodologyService } from '../../../core/methodology/methodology.service';
import { initPersonalityData } from '../../../core/personality/init-personality';
import { PersonalityService } from '../../../core/personality/personality.service';
import { ProductService } from '../../../core/product/product.service';
import { getPeakMojoProductDescription } from '../../../core/prompt/products/peak-mojo.prompt';
import { Resume } from '../../../core/resume/resume';
import { ResumeService } from '../../../core/resume/resume.service';
import { SessionTemplate, SessionTemplateUnSafeSubData } from '../../../core/session-template/session-template';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { SessionCreativity, SessionPerformance } from '../../../core/session/session';
import { UserClerkService } from '../../../core/user/user-clerk.service';
import { WorkspaceHolderService } from '../../../core/workspace/workspace.holder.service';
import { ProfileCardComponent } from '../../../domain/profile-card/profile-card.component';
import { SessionTemplateCardComponent } from '../../../domain/session-template/session-template-card/session-template-card.component';
import { surveyAnimations } from '../../../shared/animation/survey.animation';

@Component({
  selector: 'app-onboarding-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzRadioModule,
    NzSelectModule,
    NzSegmentedModule,
    NzPopconfirmModule,
    NzCheckboxModule,
    NzProgressModule,
    NzDividerModule,
    NzAutocompleteModule,
    NzSkeletonModule,
    MatTooltipModule,

    SessionTemplateCardComponent,
    ProfileCardComponent,
  ],
  templateUrl: './onboarding-modal.component.html',
  styleUrl: './onboarding-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: surveyAnimations,
})
export class OnboardingModalComponent {
  @Input() isVisible = false;
  @Output() isVisibleChange = new EventEmitter<boolean>();

  isLoading = signal<boolean>(false);

  totalSteps = 1;
  currentStepIndex = signal<number>(0);

  sessionTemplate = signal<SessionTemplate | undefined>(undefined);

  private saveSubject = new Subject<void>();

  jobCategoryList = [
    'Early Career Sellers',
    'Startup/ Small and Medium-sized Businesses Founders',
    'Account Executives',
    'Sales Enablement/ Sales Trainer',
    'Sales Leadership/ Management',
    'Other',
  ];

  jobCategoryOther = '';

  surveyData: OnboardingSurveyData = {
    jobCategory: '',
    whereHearUs: '',
    productOrServiceToSell: getPeakMojoProductDescription(),
  };

  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });
  sessionTemplateList = toSignal(this.sessionTemplateService.itemList$(), { initialValue: [] });

  getResume = (id?: string) => computed(() => this.resumeList().find((resume) => resume.id === id));

  onFillOutProductClick() {
    this.surveyData.productOrServiceToSell = getPeakMojoProductDescription();
  }

  ngOnInit(): void {
    this.loadSurveyResponse();

    this.saveSubject.pipe(debounceTime(300)).subscribe(() => {
      localStorage.setItem('surveyResponse', JSON.stringify(this.surveyData));
    });
  }

  constructor(
    private workspaceHolderService: WorkspaceHolderService,
    private userClerkService: UserClerkService,
    private router: Router,
    private msg: NzMessageService,
    private goalService: GoalService,
    private productService: ProductService,
    private resumeService: ResumeService,
    private coreItemService: CoreItemService,
    private methodologyService: MethodologyService,
    private sessionTemplateService: SessionTemplateService,
    private personalityService: PersonalityService
  ) {}

  currentProgress = computed(() => {
    return Math.round((this.currentStepIndex() / this.totalSteps) * 100);
  });

  onPreviousClick() {
    this.currentStepIndex.set(this.currentStepIndex() - 1);
  }

  onNextClick() {
    if (this.currentStepIndex() === 0) {
      if (!this.surveyData.productOrServiceToSell || this.surveyData.productOrServiceToSell?.length < 5) {
        this.msg.error(
          'We will customize your AI customer based on your response. Please enter a product or service with at least 5 letters.',
          { nzDuration: 5000 }
        );
        return;
      } else {
        this.isLoading.set(true);
        this.productService
          .verifyProductDescription$(this.surveyData.productOrServiceToSell)
          .pipe(take(1))
          .subscribe((result) => {
            if (!result.isValidProduct) {
              this.msg.error(result.explanation, { nzDuration: 5000 });
            } else {
              this.currentStepIndex.set(this.currentStepIndex() + 1);
            }
            this.isLoading.set(false);
            return;
          });
        return;
      }
    }

    this.currentStepIndex.set(this.currentStepIndex() + 1);
  }

  onCancelClick() {
    this.isVisible = false;
    this.isVisibleChange.emit(this.isVisible);

    this.router.navigate(['/scenario-library']);
  }

  onIsVisibleChange(isVisible: boolean) {
    this.isVisibleChange.emit(isVisible);
  }

  onFinishClick() {
    this.isLoading.set(true);

    if (this.surveyData.jobCategory === 'Other') {
      this.surveyData.jobCategory = this.jobCategoryOther;
    }

    this.userClerkService.reloadUser$().pipe(take(1)).subscribe();

    this.currentStepIndex.set(this.currentStepIndex() + 1);

    forkJoin({
      product: this.productService.createProductByDescription$(this.surveyData.productOrServiceToSell),
      resume: this.resumeService.createResumeByProduct$(this.surveyData.productOrServiceToSell),
      methodologyList: this.methodologyService.addMethodologyList$(initMethodologyData),
      goal: this.goalService.addGoal$('Book a meeting', `Book a future call to demo your product.`, {}),
      personalityList: this.personalityService.addList$(initPersonalityData),
    })
      .pipe(
        mergeMap(({ product, resume, methodologyList, goal, personalityList }) => {
          (resume as Resume).subData.content!.personalityIdList = [
            personalityList.find((c) => c.title === 'Rude')!.id,
            personalityList.find((c) => c.title === 'Brief')!.id,
          ];

          this.resumeService.updateResume$(resume).pipe(take(1)).subscribe();

          const sessionTemplateUnsafeSubData: SessionTemplateUnSafeSubData = {
            language: 'en',

            salesCycleId: this.workspaceHolderService.currentWorkspace().settings?.salesCycleList[0].id,
            goalId: goal.id,
            methodologyId: methodologyList[0].id,
            resumeId: resume.id,
            productId: product.id,

            isFamiliarProduct: false,

            performance: SessionPerformance.Quality,
            creativity: SessionCreativity.Creative,
          };

          return this.sessionTemplateService.addWithoutTitle$(resume, goal, product, sessionTemplateUnsafeSubData);
        })
      )
      .subscribe((sessionTemplate: SessionTemplate) => {
        this.deleteSurveyResponse();

        this.sessionTemplate.set(sessionTemplate);

        this.isLoading.set(false);

        this.router.navigate(['/scenario-library']);
      });
  }

  onJoinOurDiscordClick() {
    window.open('https://discord.gg/weytECP3BW', '_blank');
  }

  saveSurveyResponse(value?: string): void {
    this.saveSubject.next();
  }

  private loadSurveyResponse(): void {
    const savedResponse = localStorage.getItem('surveyResponse');
    if (savedResponse) {
      this.surveyData = JSON.parse(savedResponse);

      // if (this.surveyData.jobCategory) {
      //   this.currentStepIndex.set(1);
      // }

      // if (this.surveyData.productOrServiceToSell) {
      //   this.currentStepIndex.set(2);
      // }
    }
  }

  deleteSurveyResponse(): void {
    localStorage.removeItem('surveyResponse');
  }
}
